<template>
  <div class="pa-admin sv-admin">
    <div class="paa-subnav" role="tablist">
      <button
        v-for="tab in tabs"
        :key="tab.key"
        type="button"
        role="tab"
        class="paa-subnav__tab"
        :class="{ 'paa-subnav__tab--active': activeTab === tab.key }"
        :aria-selected="activeTab === tab.key ? 'true' : 'false'"
        @click="activeTab = tab.key"
      >{{ tab.label }}</button>
    </div>
    <div class="paa-main">
      <serp-validation-history v-if="activeTab === 'history'" key="history" review="all" />
      <serp-validation-history v-else-if="activeTab === 'queue'" key="queue" review="queue" />
      <serp-validation-tuning v-else-if="activeTab === 'tuning'" />
    </div>
  </div>
</template>

<script>
import SerpValidationHistory from './History.vue'
import SerpValidationTuning from './Tuning.vue'

const TABS = [
  { key: 'history', label: 'History' },
  { key: 'queue', label: 'Review Queue' },
  { key: 'tuning', label: 'Tuning' },
]

/**
 * Admin > Products > SERP Validation: the log of the Google SERP check (strategy v4,
 * helpers/serp_validation.py). History lists every checked competitor fetch; Review Queue the
 * borderline pages, the passed sample, short lists and uncertain runs waiting for a manual verdict;
 * Tuning counts the stored pages under each threshold. `?activeTab=serpValidation&svTab=queue`.
 */
export default {
  name: 'AdminSerpValidation',
  components: { SerpValidationHistory, SerpValidationTuning },
  data () {
    return { activeTab: 'history', tabs: TABS }
  },
  mounted () {
    const wanted = this.$route.query.svTab
    if (wanted && TABS.some((tab) => tab.key === wanted)) this.activeTab = wanted
  },
}
</script>

<style lang="scss">
@import '../PopAuthority/paAdmin';

.sv-admin {
  .sv-tiles {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
    gap: 10px;
    margin: 12px 0;
  }
  .sv-tile {
    background: #fff;
    border: 1px solid #e6e8ec;
    border-radius: 8px;
    padding: 10px 12px;
    b { display: block; font-size: 20px; line-height: 1.2; }
    span { font-size: 12px; color: #6b7280; }
  }
  .sv-score {
    display: inline-block;
    min-width: 42px;
    padding: 1px 6px;
    border-radius: 10px;
    text-align: center;
    font-variant-numeric: tabular-nums;
    &--fail { background: #fde8e8; color: #b42318; }
    &--borderline { background: #fff4d6; color: #93520a; }
    &--ok { background: #e3f6e8; color: #166534; }
    &--empty { background: #f1f2f4; color: #6b7280; }
  }
  .sv-detail {
    background: #fafbfc;
    td { padding: 12px 14px; }
  }
  .sv-detail-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
    gap: 14px;
  }
  .sv-rows {
    margin: 6px 0 0;
    padding: 0;
    list-style: none;
    li { padding: 4px 0; border-bottom: 1px solid #eef0f3; font-size: 12px; line-height: 1.35; }
    li.sv-row--miss { opacity: 0.55; }
    a { word-break: break-all; }
  }
  .sv-verdict {
    display: flex;
    gap: 6px;
    align-items: center;
    flex-wrap: wrap;
    margin-top: 10px;
  }
  .sv-bar {
    height: 10px;
    background: #f97316;
    border-radius: 3px;
    display: inline-block;
    vertical-align: middle;
  }
  .sv-bar--p2 { background: #60a5fa; }
  .sv-mode {
    font-size: 12px;
    color: #6b7280;
    margin-left: 8px;
  }
  .sv-tip {
    cursor: help;
    border-bottom: 1px dotted currentColor;
  }
  .sv-intro {
    padding: 10px 14px;
    margin: 12px 0 4px;
    font-size: 13px;
    line-height: 1.45;
    color: #374151;
  }
}

/* The tooltip card renders at body level (Vuetify), so these rules live outside .sv-admin. */
.white-bg-tooltip .sv-tip-line + .sv-tip-line { margin-top: 6px; }
.white-bg-tooltip .sv-chip {
  display: inline-block;
  padding: 1px 8px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  margin-right: 6px;
  white-space: nowrap;
  vertical-align: baseline;
}
/* score bands: same colours as the Page columns */
.white-bg-tooltip .sv-chip--score-fail { background: #fde8e8; color: #b42318; }
.white-bg-tooltip .sv-chip--score-borderline { background: #fff4d6; color: #93520a; }
.white-bg-tooltip .sv-chip--score-ok { background: #e3f6e8; color: #166534; }
.white-bg-tooltip .sv-chip--score-empty { background: #f1f2f4; color: #6b7280; }
/* scenarios and verdicts: same colours as the badges (paAdmin suc / info / warn / dan) */
.white-bg-tooltip .sv-chip--scenario-A { background: #D6FBEE; color: #1c805d; }
.white-bg-tooltip .sv-chip--scenario-B { background: #E9F3FF; color: #1f4fcc; }
.white-bg-tooltip .sv-chip--scenario-C { background: #FAF3D0; color: #8a7c2f; }
.white-bg-tooltip .sv-chip--scenario-D { background: #FBE8E8; color: #d12626; }
.white-bg-tooltip .sv-chip--verdict-healthy { background: #D6FBEE; color: #1c805d; }
.white-bg-tooltip .sv-chip--verdict-poisoned { background: #FBE8E8; color: #d12626; }
.white-bg-tooltip .tooltip-heading { font-weight: 700; }
.white-bg-tooltip .tooltip-discription { font-size: 13px; line-height: 1.4; }
</style>
